import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './About.css';

// The current app version, shown in the About header.
// TICKET https://github.com/ununhexium/autolang/issues/329
const APP_VERSION = '0.1.3';

// One or more screenshots that are collapsed by default and revealed on demand,
// so the notes stay compact. Drop it anywhere in a release; the images are only
// loaded once the user asks to see them. Pass a single `src`/`alt`, or an
// `images` array of { src, alt } to group several under one toggle.
function CollapsibleImage({ src, alt, images }) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);

  const figures = images ?? [{ src, alt }];
  const multiple = figures.length > 1;
  const toggleLabel = shown
    ? (multiple ? t('about.hideScreenshots') : t('about.hideScreenshot'))
    : (multiple ? t('about.showScreenshots') : t('about.showScreenshot'));

  return (
    <div className="about-release-figure">
      <button
        type="button"
        className="about-image-toggle"
        aria-expanded={shown}
        onClick={() => setShown((v) => !v)}
      >
        {toggleLabel}
      </button>
      {shown &&
        figures.map((figure, i) => (
          <img
            key={figure.src ?? i}
            className="about-release-image"
            src={figure.src}
            alt={figure.alt}
            loading="lazy"
          />
        ))}
    </div>
  );
}

// The release notes are written as plain markup below rather than generated from
// a data structure, so screenshots and other content can be dropped in anywhere
// inside a release. These are user-facing notes: curate only what matters to a
// learner. Screenshots live under public/release/<version>/ and are served from
// the site root. Add a new <section> at the top for each release.
function About() {
  const { t } = useTranslation();
  return (
    <div className="about-container">
      <div className="about-header">
        <h2>{t('about.title')}</h2>
      </div>

      <div className="about-content">
        <div className="about-app">
          <span className="about-app-name">Autolang</span>
          <span className="about-version">{t('about.version', { version: APP_VERSION })}</span>
        </div>

        <div className="about-notes">
          <h3 className="about-notes-title">{t('about.releaseNotes')}</h3>

          <section className="about-release">
            <h4 className="about-release-version">0.1.3</h4>
            <ul className="about-release-changes">
              <li>Scenes: instead of only chatting, you step into a situation and have something to get done in the language.</li>
              <li>The bakery, in French: you are given a shopping list and a wallet, and you buy what you can from a baker who speaks no English. One thing on the list is always sold out, so you have to cope with being told no.</li>
              <li>The Hauptbahnhof, in German: take a numbered slip and listen for the counter you are called to, buy a ticket that matches what you were asked for, find your platform on the departure board, pick up a few things at the kiosk, and board your train before it pulls out.</li>
              <li>The station clock runs while you play, so walking to the wrong platform costs you minutes you may need later. Reading the transcript or asking the teacher costs you nothing.</li>
              <li>Replay a scenario from any scene you have already reached, instead of starting again from the beginning every time.</li>
              <li>Speak out loud: hold the button to talk, and the persona answers in a voice of its own — young or adult, feminine or masculine.</li>
              <li>Ask the teacher out loud in the middle of a scene, in your own language, without losing your place in the conversation.</li>
              <li>Everything said out loud is written down as you go, and every conversation you have in a scene is kept to read back under Scenes.</li>
              <li>The feedback screen opens on a suggested amount rather than an empty box, and you can read back everything you have already sent.</li>
              <li>The bakery counter shows a drawing of each item instead of an emoji, so a croissant and a pain au chocolat no longer look the same.</li>
            </ul>
          </section>

          <section className="about-release">
            <h4 className="about-release-version">0.1.2</h4>
            <ul className="about-release-changes">
              <li>Archive the chats and personae you are done with: they leave your lists but stay readable, and you can restore them at any time.</li>
              <li>Tell Autolang how good you are in each language you study, so it can pitch its answers at your level.</li>
              <li>The web app is now available in English, German, French and Thai.</li>
              <li>Stay signed in with “remember me”, and sign out sessions you no longer use.</li>
            </ul>
          </section>

          <section className="about-release">
            <h4 className="about-release-version">0.1.1</h4>
            <ul className="about-release-changes">
              <li>Translate a single message on demand while chatting.</li>
              <li>
                <CollapsibleImage
                  images={[
                    { src: '/release/0.1.1/translation_de_CH_de_DE_menu.webp', alt: 'Translation menu' },
                    { src: '/release/0.1.1/translation_de_CH_de_DE_result.webp', alt: 'Translation result' },
                    { src: '/release/0.1.1/translation_jp_en_result.webp', alt: 'Translation result' },
                  ]}
                />
              </li>
              <li>Build a persona with the help of AI.</li>
              <li>
                <CollapsibleImage src="/release/0.1.1/persona_creation.webp" alt="Persona creation assistant chat" />
              </li>
              <li>The corrected sentence and the teacher’s comment now show up reliably.</li>
              <li>
                <CollapsibleImage src="/release/0.1.1/teacher_comment.webp" alt="Teacher comment now visible" />
              </li>
            </ul>
          </section>

          <section className="about-release">
            <h4 className="about-release-version">0.1.0</h4>
            <ul className="about-release-changes">
              <li>Create your own personas directly in the web app.</li>
              <li>
                <CollapsibleImage src="/release/0.1.0/chats.webp" alt="The chats list" />
              </li>
              <li>Dark theme.</li>
              <li>
                <CollapsibleImage src="/release/0.1.0/dark_light.webp" alt="Light/dark theme" />
              </li>
              <li>See how your translated sentences line up with the original.</li>
              <li>
                <CollapsibleImage src="/release/0.1.0/correlation.webp" alt="Correlation between the words of 2 sentences" />
              </li>
              <li>Discover which languages are supported.</li>
              <li>
                <CollapsibleImage src="/release/0.1.0/languages.webp" alt="Languages list" />
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

export default About;
